import {
  PendingDocumentsResponseDTO,
  CompletedDocumentsResponseDTO,
  UploadResponseDTO,
} from "../models/DocumentDTOs";
import { DocumentListFilters } from "../../domain/entities/Document";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL, CORE_API_URL } from "../../../../config/api";

/**
 * API Error Class
 */
export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: any) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Upload documents (invoices/bills)
 */
export const uploadDocumentsApi = async (
  formData: FormData
): Promise<UploadResponseDTO> => {
  const url = `${CORE_API_URL}/invoice_processing_2_0.php?api=upload`;

  try {
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message || `Upload failed: ${response.statusText}`,
        response.status,
        data
      );
    }

    return {
      success: true,
      message: data.message || "Upload successful",
      data: data.data,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const err = error as Error;
    throw new ApiError(err.message || "Network error occurred", 0);
  }
};

/**
 * Upload bank/credit card statement
 */
export const uploadStatementApi = async (
  file: File,
  source: "bank" | "credit",
  bankName: string,
  zohoBankAccountId: string
): Promise<UploadResponseDTO> => {
  const url = `${API_BASE_URL}/api/data/statementUpload`;
  const token = getToken();

  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }

  // Get user data for Zoho refresh token
  const userData = localStorage.getItem("user_data");
  if (!userData) {
    throw new ApiError("User data not found", 401);
  }

  const zohoRefreshToken = localStorage.getItem("zoho_refresh_token");

  if (!zohoRefreshToken) {
    throw new ApiError("No Zoho refresh token found in user data", 401);
  }

  const companyData = localStorage.getItem("company_data");
  if (!companyData) {
    throw new ApiError("Company data not found", 400);
  }

  const company = JSON.parse(companyData);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("source", source);
  formData.append("bank_name", bankName);
  formData.append("companyid", company.zoho_org_id);
  formData.append("zoho_bankaccountid", zohoBankAccountId);
  formData.append("zoho_refresh_token", zohoRefreshToken);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    // Check for HTTP errors
    if (!response.ok) {
      throw new ApiError(
        data.message || `Statement upload failed: ${response.statusText}`,
        response.status,
        data
      );
    }

    // Check for business logic errors (e.g., account_mismatch)
    if (data.success === false) {
      throw new ApiError(
        data.message || "Statement upload failed",
        response.status,
        data
      );
    }

    return {
      success: true,
      message: data.message || "Statement uploaded successfully",
      data: data.data,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const err = error as Error;
    throw new ApiError(err.message || "Network error occurred", 0);
  }
};

/**
 * Download bank statement Excel template
 */
export const downloadStatementExcelTemplateApi = async (): Promise<void> => {
  const url = `${API_BASE_URL}/api/data/statementExcelTemplate`;
  const token = getToken();

  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message ||
        `Failed to download template: ${response.statusText}`,
      response.status,
      errorData
    );
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  const isExcel =
    contentType.includes("spreadsheetml.sheet") ||
    contentType.includes("application/vnd.openxmlformats");
  if (!isExcel) {
    const text = await response.text();
    let err: { message?: string } = {};
    try {
      err = JSON.parse(text);
    } catch {
      err = { message: "Server did not return an Excel file." };
    }
    throw new ApiError(
      err.message ?? "Server did not return an Excel file.",
      response.status,
      err
    );
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = "bank_statement_template.xlsx";
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename=([^;]+)/i);
    if (filenameMatch) {
      filename = (filenameMatch[1] ?? filenameMatch[2]).trim();
    }
  }

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  document.body.removeChild(a);
};

/**
 * Upload bank/credit card statement (Excel file)
 */
export const uploadStatementExcelApi = async (
  file: File,
  source: "bank" | "credit",
  bankName: string,
  zohoBankAccountId: string
): Promise<UploadResponseDTO> => {
  const url = `${API_BASE_URL}/api/data/statementUploadExcel`;
  const token = getToken();

  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }

  const userData = localStorage.getItem("user_data");
  if (!userData) {
    throw new ApiError("User data not found", 401);
  }

  const zohoRefreshToken = localStorage.getItem("zoho_refresh_token");
  if (!zohoRefreshToken) {
    throw new ApiError("No Zoho refresh token found in user data", 401);
  }

  const companyData = localStorage.getItem("company_data");
  if (!companyData) {
    throw new ApiError("Company data not found", 400);
  }

  const company = JSON.parse(companyData);

  const formData = new FormData();
  formData.append("file", file);
  formData.append("source", source);
  formData.append("bank_name", bankName);
  formData.append("companyid", company.zoho_org_id);
  formData.append("zoho_bankaccountid", zohoBankAccountId);
  formData.append("zoho_refresh_token", zohoRefreshToken);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.message || `Statement upload failed: ${response.statusText}`,
      response.status,
      data
    );
  }

  if (data.success === false) {
    throw new ApiError(
      data.message || "Statement upload failed",
      response.status,
      data
    );
  }

  return {
    success: true,
    message: data.message || "Statement uploaded successfully",
    data: data.data,
  };
};

/**
 * Fetch pending documents (combines invoices and statements)
 */
export const fetchPendingDocumentsApi = async (
  limit: number,
  offset: number,
  filters: DocumentListFilters = {}
): Promise<PendingDocumentsResponseDTO> => {
  return fetchDocumentsApi("pending", limit, offset, filters);
};

/**
 * Fetch completed documents (balanced 5+5: up to half from invoices/bills, half from statements).
 * Ensures bank statements are visible even when there are many more invoices.
 */
export const fetchCompletedDocumentsApi = async (
  limit: number,
  offset: number,
  filters: DocumentListFilters = {}
): Promise<CompletedDocumentsResponseDTO> => {
  return fetchDocumentsApi("processed", limit, offset, filters);
};

const fetchDocumentsApi = async <
  T extends PendingDocumentsResponseDTO | CompletedDocumentsResponseDTO,
>(
  state: "pending" | "processed",
  limit: number,
  offset: number,
  filters: DocumentListFilters = {}
): Promise<T> => {
  const token = getToken();

  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }

  const page = Math.floor(offset / limit) + 1;
  const params = new URLSearchParams({
    state,
    type: filters.type || "all",
    page: String(page),
    limit: String(limit),
    sort_by: "created_at",
    sort_direction: "DESC",
  });

  if (filters.dateRange) params.set("date_range", filters.dateRange);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.search?.trim()) params.set("search", filters.search.trim());

  try {
    const response = await fetch(`${API_BASE_URL}/api/data/documents?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message || `Failed to fetch documents: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const err = error as Error;
    throw new ApiError(err.message || "Failed to fetch documents", 0);
  }
};

/**
 * Retry invoice processing
 */
export const retryInvoiceProcessingApi = async (
  jobId: string,
  retryType: "quick" | "full"
): Promise<{ success: boolean; message: string }> => {
  const url = `${API_BASE_URL}/api/data/invoices/retry`;
  const token = getToken();

  if (!token) {
    throw new ApiError("No authentication token found", 401);
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        job_id: jobId,
        retry_type: retryType,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message || `Retry failed: ${response.statusText}`,
        response.status,
        data
      );
    }

    return {
      success: true,
      message: data.message || "Retry initiated successfully",
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    const err = error as Error;
    throw new ApiError(err.message || "Network error occurred", 0);
  }
};
